/**
 * Bundle 141 - Generic admin section landing page.
 *
 * Renders section title + purpose, a card grid (one card per
 * sub-item the section owns), and a recent activity feed. Consumed
 * by AdminOperationsLandingPage, AdminAnalyticsLandingPage,
 * AdminSettingsLandingPage, and AdminToolsLandingPage.
 *
 * Data source: GET /api/admin/nav/section/{section}
 *
 * Guardrails: no em/en dashes; no eslint-disable; no photo/image in
 * HTML alt attributes.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Clock, RefreshCw, Inbox } from 'lucide-react';

const ActivityRow = ({ row }) => (
  <div
    data-testid="section-activity-row"
    className="flex items-start gap-3 py-2 text-sm border-b border-neutral-100 last:border-b-0"
  >
    <Clock className="h-3.5 w-3.5 mt-1 text-neutral-500 shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="font-mono text-xs text-neutral-800 truncate">
        {row.action}
      </div>
      {row.note ? (
        <div className="text-xs text-neutral-700 truncate">
          {row.note}
        </div>
      ) : null}
    </div>
    <div className="text-xs text-neutral-600 shrink-0">
      {row.when || ''}
    </div>
  </div>
);

const SectionCard = ({ card }) => {
  const hasCount = card.count !== null && card.count !== undefined;
  return (
    <Card data-testid={`section-card-${card.key}`}>
      <CardContent className="p-5 flex flex-col h-full gap-3">
        <div>
          <h3 className="text-base font-semibold text-neutral-900 mb-1">
            {card.name}
          </h3>
          <p className="text-sm text-neutral-600">{card.description}</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-neutral-700 mt-1">
          {hasCount ? (
            <Badge
              variant="outline"
              data-testid={`section-card-count-${card.key}`}
              className="bg-neutral-50 text-neutral-800"
            >
              {card.count} {card.count_label || ''}
            </Badge>
          ) : null}
          {card.last_activity ? (
            <span
              data-testid={`section-card-last-activity-${card.key}`}
              className="inline-flex items-center gap-1 text-neutral-700"
            >
              <Clock className="h-3 w-3" />
              last {card.last_activity}
            </span>
          ) : null}
        </div>
        <div className="flex-1" />
        <Link to={card.cta}>
          <Button
            size="sm"
            variant="outline"
            className="w-full justify-between"
            data-testid={`section-card-cta-${card.key}`}
          >
            {card.cta_label || 'Open'}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
};

const AdminSectionLandingPage = ({ section, testidPrefix }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: d } = await api.get(
        `/admin/nav/section/${section}`);
      setData(d);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load section');
    } finally {
      setLoading(false);
    }
  }, [section]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div
        className="p-6 text-neutral-500"
        data-testid={`${testidPrefix}-loading`}
      >
        Loading section...
      </div>
    );
  }
  if (error) {
    return (
      <div
        className="p-6 text-red-600"
        data-testid={`${testidPrefix}-error`}
      >
        {error}
      </div>
    );
  }
  const cards = data?.cards || [];
  const feed = data?.recent_activity || [];

  return (
    <div
      className="p-6 max-w-6xl mx-auto"
      data-testid={`${testidPrefix}-root`}
    >
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1
            className="text-2xl font-bold text-neutral-900"
            data-testid={`${testidPrefix}-title`}
          >
            {data?.title || section}
          </h1>
          <p
            className="text-sm text-neutral-700 mt-1"
            data-testid={`${testidPrefix}-purpose`}
          >
            {data?.purpose}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          data-testid={`${testidPrefix}-refresh`}
        >
          <RefreshCw className="h-4 w-4 mr-1" />
          Refresh
        </Button>
      </div>

      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8"
        data-testid={`${testidPrefix}-cards`}
      >
        {cards.map((c) => (
          <SectionCard key={c.key} card={c} />
        ))}
      </div>

      <div>
        <h2 className="text-base font-semibold text-neutral-800 mb-2 flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Recent activity
        </h2>
        <Card>
          <CardContent
            className="p-4"
            data-testid={`${testidPrefix}-activity`}
          >
            {feed.length === 0 ? (
              <div
                className="flex flex-col items-center gap-2 py-6 text-sm text-neutral-700"
                data-testid={`${testidPrefix}-activity-empty`}
              >
                <Inbox className="h-6 w-6 text-neutral-400" />
                No recent admin actions in this section yet.
              </div>
            ) : (
              <div>
                {feed.map((row, i) => (
                  <ActivityRow key={i} row={row} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminSectionLandingPage;
